/**
 * TK-13530 coordination: the auth-client stub must not wipe Better Auth's shared session store
 * when `refetchOnWindowFocus` runs on purchase-designer routes. Profile sync must wait for the
 * leave-flow resync before reacting to a stale logged-out session.
 *
 * Delete with the rest of the injunction (see `isEcommerceDesignerRoute.ts`).
 */

type BetterAuthSessionData = {
    session?: unknown
    user?: { id?: string; isAnonymous?: boolean } | null
}

let preservedGetSessionBody: string | null = null
let leaveResyncPromise: Promise<void> | null = null

export function isBetterAuthGetSessionRequest(url: string): boolean {
    return url.includes('/get-session')
}

/** Snapshot a real session before focus/refetch stubs would replace it with null. */
export function syncPreservedBetterAuthSessionFromClientData(
    data: BetterAuthSessionData | null | undefined
): void {
    const user = data?.user
    if (!user?.id) {
        return
    }
    preservedGetSessionBody = JSON.stringify({
        session: data?.session ?? null,
        user,
    })
}

export function clearPreservedBetterAuthSession(): void {
    preservedGetSessionBody = null
}

export function getPreservedBetterAuthGetSessionStubResponse(): Response | null {
    if (!preservedGetSessionBody) {
        return null
    }
    return new Response(preservedGetSessionBody, {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
    })
}

/**
 * Runs async leave-flow Better Auth restore; exposes the same promise so profile-auth-sync can
 * await it before clearing profile on a corrupted session snapshot.
 */
export function runLeaveEcommerceDesignerBetterAuthResync(task: () => Promise<void>): void {
    const promise = task().finally(() => {
        if (leaveResyncPromise === promise) {
            leaveResyncPromise = null
        }
        clearPreservedBetterAuthSession()
    })
    leaveResyncPromise = promise
}

export async function awaitLeaveEcommerceDesignerBetterAuthResync(): Promise<void> {
    if (leaveResyncPromise) {
        await leaveResyncPromise
    }
}
